import type HyperDX from "@hyperdx/browser";
import escapeRegExp from "lodash/escapeRegExp";

type HyperDXClient = typeof HyperDX;

let hyperDXClient: HyperDXClient | undefined;
let hyperDXInitialization: Promise<HyperDXClient | undefined> | undefined;

const isHyperDXEnabled = () => Boolean(window.HYPERDX_API_KEY);

const getHyperDXClient = async () => {
  if (!isHyperDXEnabled()) return undefined;
  if (hyperDXClient) return hyperDXClient;

  hyperDXInitialization ??= import(
    /* webpackChunkName: "hyperdx_browser" */ "@hyperdx/browser"
  )
    .then(({ default: HyperDX }) => {
      const apiKey = window.HYPERDX_API_KEY;
      if (!apiKey) return undefined;

      // Send telemetry to the origin hosting the app. This supports custom domains
      // and keeps logs and traces on the community URL instead of HyperDX's default.
      const hyperDXUrl = window.location.origin;
      const escapedOrigin = escapeRegExp(hyperDXUrl);

      HyperDX.init({
        apiKey,
        url: hyperDXUrl,
        logsUrl: `${hyperDXUrl}/v1/logs`,
        tracesUrl: `${hyperDXUrl}/v1/traces`,
        service: "circle-frontend",
        tracePropagationTargets: [new RegExp(`^${escapedOrigin}(?:/|$)`)],
        consoleCapture: true,
        advancedNetworkCapture: true,
        otelResourceAttributes: {
          "service.version": window.CIRCLE_APP_VERSION ?? "",
          "deployment.environment": window.RAILS_ENV ?? "",
        },
      });

      hyperDXClient = HyperDX;
      return HyperDX;
    })
    .catch(() => undefined);

  return hyperDXInitialization;
};

export const initializeHyperDX = () => {
  void getHyperDXClient();
};

export const setHyperDXUser = (
  id: number | string,
  communityId?: number | string,
) => {
  void getHyperDXClient().then(HyperDX => {
    HyperDX?.setGlobalAttributes({
      userId: String(id),
      communityId: communityId === undefined ? "" : String(communityId),
    });
  });
};

export const clearHyperDXUser = () => {
  void getHyperDXClient().then(HyperDX => {
    HyperDX?.setGlobalAttributes({ userId: "", communityId: "" });
  });
};
